import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

/* ===============================
   API BASE
================================ */
const API_URL = `${process.env.REACT_APP_API_URL}/product-category`;

/* ===============================
   THUNKS
================================ */

/* 🔹 CREATE */
export const createProductCategory = createAsyncThunk(
    "productCategory/create",
    async (payload, { rejectWithValue }) => {
        try {
            const res = await axios.post(
                `${API_URL}/create`,
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("token")}`,
                    },
                }
            );
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || err.message);
        }
    }
);

/* 🔹 GET ALL */
export const fetchProductCategories = createAsyncThunk(
    "productCategory/fetchAll",
    async (_, { rejectWithValue }) => {
        try {
            const res = await axios.get(`${API_URL}/all`);
            return res.data.data;
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || err.message);
        }
    }
);

/* 🔹 GET SINGLE */
export const fetchSingleProductCategory = createAsyncThunk(
    "productCategory/fetchSingle",
    async (id, { rejectWithValue }) => {
        try {
            const res = await axios.get(`${API_URL}/single/${id}`);
            return res.data.data;
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || err.message);
        }
    }
);

/* 🔹 UPDATE */
export const updateProductCategory = createAsyncThunk(
    "productCategory/update",
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const res = await axios.put(
                `${API_URL}/update/${id}`,
                data,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("token")}`,
                    },
                }
            );
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || err.message);
        }
    }
);

/* 🔹 DELETE (SOFT) */
export const deleteProductCategory = createAsyncThunk(
    "productCategory/delete",
    async (id, { rejectWithValue }) => {
        try {
            const res = await axios.delete(
                `${API_URL}/delete/${id}`,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("token")}`,
                    },
                }
            );
            return { id, message: res.data.message };
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || err.message);
        }
    }
);

/* ===============================
   SLICE
================================ */
const productCategorySlice = createSlice({
    name: "productCategory",
    initialState: {
        categories: [],
        singleCategory: null,
        loading: false,
        error: null,
        successMessage: null,
    },

    reducers: {
        clearProductCategoryState: (state) => {
            state.error = null;
            state.successMessage = null;
        },
    },

    extraReducers: (builder) => {
        builder

            /* ===== CREATE ===== */
            .addCase(createProductCategory.pending, (state) => {
                state.loading = true;
            })
            .addCase(createProductCategory.fulfilled, (state, action) => {
                state.loading = false;
                state.categories.unshift(action.payload.data);
                state.successMessage = action.payload.message;
            })
            .addCase(createProductCategory.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            /* ===== GET ALL ===== */
            .addCase(fetchProductCategories.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchProductCategories.fulfilled, (state, action) => {
                state.loading = false;
                state.categories = action.payload;
            })
            .addCase(fetchProductCategories.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            /* ===== GET SINGLE ===== */
            .addCase(fetchSingleProductCategory.fulfilled, (state, action) => {
                state.singleCategory = action.payload;
            })

            /* ===== UPDATE ===== */
            .addCase(updateProductCategory.pending, (state) => {
                state.loading = true;
            })
            .addCase(updateProductCategory.fulfilled, (state, action) => {
                state.loading = false;
                state.successMessage = action.payload.message;

                state.categories = state.categories.map((cat) =>
                    cat._id === action.payload.data._id
                        ? action.payload.data
                        : cat
                );
            })
            .addCase(updateProductCategory.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            /* ===== DELETE ===== */
            .addCase(deleteProductCategory.fulfilled, (state, action) => {
                state.categories = state.categories.filter(
                    (cat) => cat._id !== action.payload.id
                );
                state.successMessage = action.payload.message;
            })
            .addCase(deleteProductCategory.rejected, (state, action) => {
                state.error = action.payload;
            });
    },
});

export const { clearProductCategoryState } =
    productCategorySlice.actions;

export default productCategorySlice.reducer;
